/**
 * Treść strony /slub — wydzielona z pages/slub.tsx jako osobny,
 * JSON-serializable config. Strona łączy sekcje narracyjne (hero, usługi,
 * realizacje, proces, kontakt) z żywym gridem bukietów ślubnych
 * (BridalBouquetsSection) — logika pobierania kolekcji Shopify zostaje
 * w kodzie, tu wydzielona jest wyłącznie statyczna treść redakcyjna.
 *
 * Znaki ' ' (twarda spacja) odpowiadają `&nbsp;` z oryginalnego JSX —
 * w configu trzeba użyć realnego znaku Unicode, bo string wstawiony przez
 * {tekst} w JSX nie dekoduje encji HTML.
 */
export interface ServiceCard {
  title: string;
  desc: string;
}

export interface Realizacja {
  style: string;
  scope: string;
  bg: string;
  dark?: boolean;
}

export interface ProcessStep {
  num: string;
  emoji: string;
  title: string;
  desc: string;
}

export interface ContactRow {
  iconKey: string;
  label: string;
  value: string;
  href: string;
}

const NBSP = ' ';

export const SLUB_CONTENT = {
  hero: {
    tagline: 'Planujecie swój wielki dzień?',
    headingLine1: 'Kwiaty na ślub',
    headingLine2Prefix: 'i ',
    headingAccent: 'dekoracje weselne',
    headingLine3: 'w Lublinie',
    body: `Od bukietu Panny Młodej i butonierek po dekoracje ceremonii i sali. Ustalmy zakres i${NBSP}kolorystykę kwiatów dopasowanych do Waszego miejsca oraz stylu uroczystości.`,
    ctaCallLabel: 'Opowiedzcie nam o swoim ślubie',
    ctaSeeLabel: 'Zobacz kierunki aranżacji',
  },
  bridalBouquets: {
    eyebrow: 'Bukiety ślubne',
    heading: 'Bukiety dla Panny Młodej',
    body: 'Każdy bukiet ślubny tworzymy z troską, dopasowując go do stylu wesela i charakteru Panny Młodej. Świeże kwiaty, wyjątkowe kompozycje.',
  },
  services: {
    eyebrow: 'Zakres realizacji',
    heading: 'Kwiaty, które tworzą cały klimat',
    body: 'Od jednego bukietu po kompletną oprawę kwiatową - pomożemy Wam stworzyć spójną całość.',
    items: [
      { title: 'Bukiet Panny Młodej', desc: 'Wyjątkowa kompozycja dopasowana do Waszego stylu - od klasycznej elegancji po nowoczesny minimalizm.' },
      { title: 'Bukiety dla druhen', desc: 'Spójne, delikatne kompozycje harmonizujące z bukietem Panny Młodej.' },
      { title: 'Butonierki', desc: 'Starannie dobrane detale florystyczne dla Pana Młodego i drużbów.' },
      { title: 'Dekoracja ceremonii', desc: 'Ołtarz, łuki kwiatowe, dekoracje ławek i wejścia.' },
      { title: 'Dekoracja stołów', desc: 'Kompozycje stołu weselnego, bankietowego i stołu Pary Młodej.' },
      { title: 'Dekoracja sali', desc: 'Łuki kwiatowe, serce z kwiatów, instalacje i dekoracje przy wejściu.' },
      { title: 'Kwiaty do auta', desc: 'Elegancka dekoracja samochodu Pary Młodej - zawsze z klasą.' },
      { title: 'Inne kwiatowe detale', desc: 'Wianki, ozdoby tortu, wiązanki i wszystkie florystyczne niespodzianki.' },
    ] satisfies ServiceCard[],
  },
  realizacje: {
    eyebrow: 'Kierunki aranżacji',
    heading: 'Tak może wyglądać Wasz dzień',
    ctaLabel: 'Zapytaj o realizację swojego ślubu',
    items: [
      { style: 'Romantyczny', scope: 'Bukiet · Butonierki · Dekoracja stołów', bg: 'linear-gradient(160deg, #e8d5c8 0%, #c8a890 60%, #a07858 100%)' },
      { style: 'Elegancki', scope: 'Bukiet · Dekoracja ceremonii · Dekoracja sali', bg: 'linear-gradient(160deg, #1e1810 0%, #2e2416 40%, #3e3020 100%)', dark: true },
      { style: 'Naturalny', scope: 'Bukiet · Bukiety druhen · Wianki · Dekoracja stołów', bg: 'linear-gradient(160deg, #d8ddd0 0%, #c4ccba 60%, #a8b49a 100%)' },
    ] satisfies Realizacja[],
  },
  process: {
    eyebrow: 'Jak to działa',
    heading: 'Jak wygląda współpraca?',
    steps: [
      { num: '01', emoji: '💬', title: 'Poznajemy Waszą wizję', desc: 'Rozmawiamy o miejscu, stylu, kolorach i tym, jak wyobrażacie sobie ten dzień.' },
      { num: '02', emoji: '🌸', title: 'Dobieramy kwiaty i styl', desc: 'Tworzymy spójną koncepcję dopasowaną do Waszej uroczystości.' },
      { num: '03', emoji: '📋', title: 'Ustalamy szczegóły', desc: 'Dopracowujemy zakres realizacji, kompozycje i wszystkie potrzebne elementy.' },
      { num: '04', emoji: '💐', title: 'Przygotowujemy ustalone kompozycje', desc: 'Tworzymy kwiaty i dekoracje zgodnie z potwierdzonym zakresem.' },
    ] satisfies ProcessStep[],
  },
  advisory: {
    quote: `„Nie musicie wiedzieć, jakie kwiaty wybrać, ile${NBSP}kompozycji będzie potrzebnych ani co${NBSP}najlepiej sprawdzi się w${NBSP}Waszym miejscu."`,
    quoteAttribution: 'Zespół Bukietowej',
    contactEyebrow: 'Pomożemy Wam',
    contactHeadingLine1: 'Nie wiecie,',
    contactHeadingLine2: 'od czego zacząć?',
    contactBody: `Spokojnie - pomożemy Wam to zaplanować. Opowiedzcie nam o swoim ślubie, a${NBSP}wspólnie ustalimy, czego potrzebujecie.`,
    contactRows: [
      { iconKey: 'mapPin', label: 'Kwiaciarnia', value: 'al. Warszawska 23/1, Lublin', href: 'https://www.google.com/maps/dir/?api=1&destination=al.+Warszawska+23+Lublin' },
      { iconKey: 'mail', label: 'E-mail', value: 'kontakt@bukietowa.pl', href: 'mailto:kontakt@bukietowa.pl' },
      { iconKey: 'phone', label: 'Telefon', value: '+48 605 030 728', href: 'tel:+48605030728' },
    ] satisfies ContactRow[],
  },
};
